import React from 'react';
import { X } from 'lucide-react';

/**
 * Reusable modal shell with:
 * - Fixed header (title + close X)
 * - Scrollable content area
 * - Fixed footer (buttons always visible)
 * - Light blue-gray background matching reference design
 */
const ModalShell = ({
  title,
  onClose,
  children,
  footer,
  maxWidth = 'max-w-2xl',
  dataTestId,
}) => {
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      data-testid={dataTestId}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`bg-[#E8F0F2] rounded-2xl shadow-2xl ${maxWidth} w-full max-h-[92vh] flex flex-col`}
      >
        {/* ── Header ── */}
        <div className="px-6 py-4 border-b border-[#d1d5db] flex justify-between items-center shrink-0">
          <h2 className="text-xl font-semibold text-[#1a1a2e]">{title}</h2>
          <button
            onClick={onClose}
            className="text-[#6b7280] hover:text-[#1a1a2e] transition-colors"
            data-testid="close-form-button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ── Scrollable Content ── */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {children}
        </div>

        {/* ── Footer ── */}
        {footer && (
          <div className="px-6 py-4 border-t border-[#d1d5db] shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

/* Shared input class for consistency across all modals */
export const inputClass =
  'w-full bg-white border border-[#d1d5db] rounded-lg px-3 py-2 text-sm text-[#1a1a2e] focus:ring-2 focus:ring-[#4A7AE0]/20 focus:border-[#4A7AE0] outline-none transition-colors';

export const selectClass =
  'w-full bg-white border border-[#d1d5db] rounded-lg px-3 py-2 text-sm text-[#1a1a2e] focus:ring-2 focus:ring-[#4A7AE0]/20 focus:border-[#4A7AE0] outline-none transition-colors';

export const textareaClass =
  'w-full bg-white border border-[#d1d5db] rounded-lg px-3 py-2 text-sm text-[#1a1a2e] focus:ring-2 focus:ring-[#4A7AE0]/20 focus:border-[#4A7AE0] outline-none transition-colors resize-none';

export const labelClass = 'block text-sm font-medium text-[#1a1a2e] mb-1.5';

export const requiredMark = <span className="text-[#EF4444]">*</span>;

export const errorBoxClass =
  'mb-4 p-3 bg-[#FEF2F2] border border-[#FCA5A5] rounded-lg text-[#EF4444] text-sm';

export default ModalShell;
